import { create } from 'zustand';
import type { ConversationCategory, SupportMessage, VendorConversation } from '../../../common/models/support.model';

export type WidgetView =
  | { kind: 'home' }
  | { kind: 'new'; category?: ConversationCategory }
  | { kind: 'thread'; id: string };

export type WidgetMode = 'closed' | 'minimized' | 'open';

interface SupportWidgetState {
  mode: WidgetMode;
  expanded: boolean;
  view: WidgetView;
  conversations: VendorConversation[];
  loaded: boolean;
  messages: Record<string, SupportMessage[]>;
  unread: number;
  /** Unsent text per conversation ("new" for a new one). */
  drafts: Record<string, string>;

  open: (view?: WidgetView) => void;
  minimize: () => void;
  close: () => void;
  toggleExpanded: () => void;
  setView: (view: WidgetView) => void;
  setConversations: (list: VendorConversation[]) => void;
  upsertConversation: (conv: VendorConversation) => void;
  setMessages: (id: string, list: SupportMessage[]) => void;
  /** Add or replace (by _id, or by a pending temp id) without duplicates. */
  addMessage: (id: string, message: SupportMessage, replaceId?: string) => void;
  removeMessage: (id: string, messageId: string) => void;
  setUnread: (n: number) => void;
  markConversationRead: (id: string) => void;
  setDraft: (key: string, text: string) => void;
  reset: () => void;
}

const sumUnread = (list: VendorConversation[]) => list.reduce((n, c) => n + (c.unread || 0), 0);

/**
 * The vendor support widget's state. A store (not component state) because
 * the launcher, notifications and deep links all open it from elsewhere.
 */
export const useSupportWidget = create<SupportWidgetState>((set) => ({
  mode: 'closed',
  expanded: false,
  view: { kind: 'home' },
  conversations: [],
  loaded: false,
  messages: {},
  unread: 0,
  drafts: {},

  open: (view) => set((s) => ({ mode: 'open', view: view ?? s.view })),
  minimize: () => set({ mode: 'minimized' }),
  close: () => set({ mode: 'closed' }),
  toggleExpanded: () => set((s) => ({ expanded: !s.expanded })),
  setView: (view) => set({ view }),
  setConversations: (list) => set({ conversations: list, loaded: true, unread: sumUnread(list) }),
  upsertConversation: (conv) =>
    set((s) => {
      const others = s.conversations.filter((c) => c._id !== conv._id);
      const list = [conv, ...others].sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
      return { conversations: list, unread: s.loaded ? sumUnread(list) : s.unread };
    }),
  setMessages: (id, list) => set((s) => ({ messages: { ...s.messages, [id]: list } })),
  addMessage: (id, message, replaceId) =>
    set((s) => {
      const current = s.messages[id];
      if (!current) return {};
      if (current.some((m) => m._id === message._id)) {
        return replaceId ? { messages: { ...s.messages, [id]: current.filter((m) => m._id !== replaceId) } } : {};
      }
      const list = replaceId && current.some((m) => m._id === replaceId)
        ? current.map((m) => (m._id === replaceId ? message : m))
        : [...current, message];
      return { messages: { ...s.messages, [id]: list } };
    }),
  removeMessage: (id, messageId) =>
    set((s) => ({ messages: { ...s.messages, [id]: (s.messages[id] || []).filter((m) => m._id !== messageId) } })),
  setUnread: (n) => set({ unread: n }),
  markConversationRead: (id) =>
    set((s) => {
      const list = s.conversations.map((c) => (c._id === id ? { ...c, unread: 0 } : c));
      return { conversations: list, unread: s.loaded ? sumUnread(list) : s.unread };
    }),
  setDraft: (key, text) => set((s) => ({ drafts: { ...s.drafts, [key]: text } })),
  reset: () => set({ mode: 'closed', expanded: false, view: { kind: 'home' }, conversations: [], loaded: false, messages: {}, unread: 0, drafts: {} }),
}));

/** Open the support widget from anywhere (launcher, command palette, a notification). */
export function openSupport(view?: WidgetView) {
  useSupportWidget.getState().open(view);
}
